// Wealth Identity Assessment — none.
// Resources articles: data + body content for the index list and detail pages.
import React from "react";

const ART1_IMG = "https://customer-assets.emergentagent.com/job_banking-systems-1/artifacts/wurxitr0_article1.png";
const ART2_IMG = "https://customer-assets.emergentagent.com/job_banking-systems-1/artifacts/gvffbhxm_article2.png";
const ART3_IMG = "https://customer-assets.emergentagent.com/job_banking-systems-1/artifacts/kwd6p4ed_article3.png";

/* Editorial body renderer helpers — kept here so articles read like documents. */
const Sub = ({ children }) => (
  <h2
    className="font-serif text-nearblack leading-[1.18] mt-14 mb-5"
    style={{ fontSize: "clamp(1.5rem, 2.6vw, 1.95rem)", fontWeight: 400 }}
  >
    {children}
  </h2>
);

const P = ({ children }) => (
  <p className="text-nearblack/85 font-light text-[17px] md:text-[17.5px] leading-[1.85] mb-6">
    {children}
  </p>
);

/* ---------------- Article bodies (JSX) ---------------- */

const Article1Body = () => (
  <>
    <P>
      There&apos;s a number most people never calculate. Not because it&apos;s hard to find — but
      because nobody ever tells them to look for it. It&apos;s the total amount of interest
      they&apos;ll pay over the course of their lifetime. Not the interest rate on any one loan.
      Not the monthly payment on a car or the APR on a credit card. The cumulative total. Every
      loan, every financed purchase, every dollar sent to a lender across an entire lifetime of
      financial decisions. For most people, that number is staggering. And almost none of it
      comes back to them.
    </P>

    <Sub>The need to finance never goes away.</Sub>
    <P>
      At some point, you will need to finance something. Probably many things. A car. A home. A
      business expense. An emergency. Education. The list is different for everyone, but the
      pattern is the same — life requires capital, and most people don&apos;t have all of it
      sitting in cash at the exact moment they need it. So they borrow. And borrowing costs money.
      This isn&apos;t a moral judgment. It&apos;s just how the system works. The question worth
      asking isn&apos;t whether you&apos;ll participate in the financing system over the course of
      your life. You will. The question is whether you&apos;ve ever stopped to add up what that
      participation is costing you — and whether there&apos;s a better way to structure it.
    </P>

    <Sub>The interest rate is the wrong number to focus on.</Sub>
    <P>
      When most people evaluate a loan, they look at the interest rate. Is 6% good? Is 4% better?
      Can I negotiate this down? The rate matters — but it&apos;s not the most important number.
      The more important number is volume. How much total interest will you pay, across how many
      loans, over how many decades? Think about it this way. The average person finances multiple
      cars over their lifetime. They carry a mortgage for thirty years. They use credit cards,
      take personal loans, finance appliances and medical bills and business equipment. Each of
      those transactions has an interest cost attached to it. Each of those interest payments
      goes somewhere. It goes to a bank. To a lender. To a finance company. To an institution
      that does exactly one thing with it — puts it back to work generating more returns for
      itself. The banking system is one of the most consistently profitable industries in the
      world. It doesn&apos;t stay that way by accident. It stays that way because the flow of
      money is structured in its favor, and most people never question whether it has to be that
      way.
    </P>

    <Sub>What banks actually do with your money.</Sub>
    <P>
      When you deposit money in a bank, the bank doesn&apos;t let it sit. It lends it out — at a
      higher rate than it&apos;s paying you. The difference between what it pays depositors and
      what it charges borrowers is called the spread. That spread, multiplied across millions of
      accounts and billions of dollars, is where bank profit comes from. You&apos;re on both
      sides of this equation without realizing it. You deposit money and earn a modest return.
      Then you borrow money and pay a higher rate. The bank profits from both transactions. You
      funded the system and paid for the privilege of using it. This isn&apos;t a conspiracy.
      It&apos;s just how banking works. The more useful question is what it would look like if
      you were on the other side of it.
    </P>

    <Sub>Recapturing the banking function.</Sub>
    <P>
      The Infinite Banking Concept is built on a simple premise: everyone has a need to finance
      things throughout their life, the banking system profits enormously from that need, and
      there is a way to redirect that profit back to yourself. The mechanics involve building a
      private banking system inside a dividend-paying whole life insurance policy from a mutual
      company — a structure that allows you to finance your own purchases, repay yourself with
      interest, and keep the compounding working in your favor instead of a lender&apos;s. The
      goal isn&apos;t to avoid borrowing. It&apos;s to change who benefits when you do.
      You&apos;re going to finance things either way. The question is whether that financing
      function is building something for you — or for someone else. Most people never ask that
      question. The ones who do tend to find that the answer changes how they think about money
      entirely.
    </P>
  </>
);

const Article2Body = () => (
  <>
    <P>
      If you&apos;ve ever brought up Infinite Banking or whole life insurance in a conversation
      with a financial advisor, there&apos;s a good chance you heard some version of the same
      response. &ldquo;Whole life is a bad investment.&rdquo; &ldquo;Buy term and invest the
      rest.&rdquo; &ldquo;The fees are too high.&rdquo; &ldquo;You can do better in the
      market.&rdquo; These objections are so common they&apos;ve become almost reflexive —
      repeated confidently, rarely examined closely. And for a lot of people, that&apos;s where
      the conversation ends. It shouldn&apos;t. Because almost every one of those objections is
      answering the wrong question.
    </P>

    <Sub>The category mismatch.</Sub>
    <P>
      The core problem with most financial advisor critiques of whole life insurance is that
      they&apos;re evaluating it as an investment — then concluding it&apos;s a poor one.
      That&apos;s a bit like evaluating a savings account by its returns and concluding it&apos;s
      a terrible stock. The comparison doesn&apos;t work because the tools aren&apos;t designed
      for the same purpose. Whole life insurance, as used in the Infinite Banking Concept, is not
      an investment vehicle. It&apos;s a banking vehicle. The distinction matters enormously,
      because the criteria you&apos;d use to evaluate a banking tool are completely different
      from the criteria you&apos;d use to evaluate an investment. A banking tool should be
      stable, liquid, guaranteed, and accessible without restriction. It should allow you to
      borrow against it without interrupting its growth. It should be private, with no government
      reporting requirements on how or when you access your money. It should compound reliably
      regardless of what markets are doing. Whole life, structured correctly, does all of those
      things. No investment account does.
    </P>

    <Sub>What &ldquo;buy term and invest the rest&rdquo; actually assumes.</Sub>
    <P>
      The &ldquo;buy term and invest the rest&rdquo; framework has been repeated so often
      it&apos;s become conventional wisdom. But it rests on a few assumptions worth examining. It
      assumes you&apos;ll actually invest the difference — consistently, without interruption,
      for decades. It assumes market returns will be strong enough and timed well enough to
      outperform what you&apos;d have built inside a whole life policy. It assumes you won&apos;t
      need to access that money before retirement. And it assumes the primary goal is maximizing
      investment return. If all of those assumptions hold, the math can work in certain
      scenarios. But most people don&apos;t invest the difference. Most people encounter
      interruptions — job changes, emergencies, life — that break the consistency the strategy
      requires. And for people whose goal isn&apos;t maximum return but rather control,
      liquidity, and the ability to finance their life without dependence on banks and lenders,
      the framework misses the point entirely. Term insurance expires. It pays out if you die
      within the term. If you don&apos;t — and most people don&apos;t — it builds nothing and
      leaves nothing. That&apos;s not a flaw necessarily, but it&apos;s worth being clear about
      what you&apos;re buying and what you&apos;re not.
    </P>

    <Sub>The training gap.</Sub>
    <P>
      Most financial advisors are trained in investment management. They understand securities,
      portfolio allocation, risk tolerance, and market-based growth strategies. That&apos;s the
      framework they&apos;ve been given, and it&apos;s genuinely useful within its own domain.
      What most of them haven&apos;t been trained in is banking strategy. The mechanics of how
      money flows through a life — how financing decisions compound over time, how the interest
      you pay on loans accumulates, how to structure a private system that keeps that value
      inside your own financial ecosystem rather than routing it to a lender. Nelson Nash, who
      wrote <em>Becoming Your Own Banker</em> and is considered the architect of the modern IBC
      movement, spent decades making this distinction. His argument was never that whole life is
      a better investment than the stock market. His argument was that comparing the two
      misunderstands what whole life is for. Infinite Banking isn&apos;t about competing with
      your investment portfolio. It&apos;s about changing how you finance your life — and keeping
      the profit from that financing for yourself instead of handing it to a bank.
    </P>

    <Sub>What to do with this information.</Sub>
    <P>
      None of this means your financial advisor is wrong about everything. A well-managed
      investment portfolio and a properly structured IBC policy aren&apos;t in competition — they
      can coexist and complement each other. Many people who practice Infinite Banking continue
      to invest. What&apos;s worth questioning is whether the reflexive dismissal of whole life
      is based on a genuine understanding of what it&apos;s designed to do — or on a category
      comparison that was never quite accurate to begin with. If you&apos;ve heard &ldquo;whole
      life is a scam&rdquo; and accepted that without examining the reasoning behind it, it might
      be worth taking another look. Not because the answer is definitely different — but because
      the question being asked probably was.
    </P>
  </>
);

const Article3Body = () => (
  <>
    <P>
      Banks are not mysterious institutions. They do one thing, very consistently, very
      profitably — and they&apos;ve been doing it for a long time. They control the flow of
      money. And they charge for that control at every step. Understanding exactly how they do it
      is the starting point for understanding why Infinite Banking exists — and why the people
      who&apos;ve used it longest tend to describe it less as a financial product and more as a
      shift in how they think about money entirely.
    </P>

    <Sub>The spread.</Sub>
    <P>
      When you deposit money in a bank, the bank pays you interest. When someone else borrows
      money from that same bank, they pay the bank interest — at a higher rate. The difference
      between those two rates is called the spread. It&apos;s the bank&apos;s margin. And
      it&apos;s the foundational mechanic behind one of the most consistently profitable
      industries in history. Here&apos;s a simplified version of how it works. You deposit
      $100,000 earning 4%. The bank lends that money to a homebuyer at 7%, a car buyer at 8%, a
      business owner at 9%, and a credit card holder at 18%. The bank is paying you 4% on the
      money it&apos;s using to generate returns that far exceed what it owes you. Meanwhile, your
      money is locked in their system — earning a fraction of what it&apos;s producing for them.
      This is not a secret. Banks publish their financials. The mechanics are well understood.
      What most people haven&apos;t done is look at their own financial life through this lens
      and ask: where do I sit in this system? And is that where I want to be?
    </P>

    <Sub>You&apos;re probably on both sides — and winning on neither.</Sub>
    <P>
      Most people interact with the banking system in two ways simultaneously. They deposit money
      and earn a modest return. And they borrow money and pay a higher rate. The bank profits
      from both transactions. The depositor funds the loans. The borrower pays the interest. The
      bank collects the spread — without taking on meaningful risk, without using its own
      capital, and without most of its customers ever thinking about the structure they&apos;re
      operating inside. This isn&apos;t a critique of banks. It&apos;s just a description of how
      they work. They&apos;ve built a system that keeps money moving in their favor, and
      they&apos;ve done it well. The more useful question is what changes if you understand that
      structure clearly — and whether there&apos;s a way to position yourself differently within
      it.
    </P>

    <Sub>What the wealthiest institutions actually do.</Sub>
    <P>
      Here&apos;s something that doesn&apos;t get discussed often enough. Banks themselves — the
      same institutions that collect your deposits and charge you interest on loans — are among
      the largest holders of whole life insurance in the country. It&apos;s called Bank Owned
      Life Insurance, or BOLI. Major banks hold billions of dollars in whole life policies as a
      core part of their own financial strategy. Not as a fringe investment. As a primary asset
      class. They use it for the same reasons anyone would use it in an Infinite Banking context:
      guaranteed growth, tax advantages, liquidity, and stable returns that don&apos;t depend on
      market performance. The strategy banks use for their own capital is the same strategy
      Nelson Nash wrote about in <em>Becoming Your Own Banker</em> — the same strategy that forms
      the foundation of Infinite Banking. The difference is that most people have never been
      shown it&apos;s available to them.
    </P>

    <Sub>Becoming the banker.</Sub>
    <P>
      The premise of Infinite Banking is straightforward. You have a lifelong need to finance
      things. The banking system profits from that need. And there is a way to build a private
      system — inside a dividend-paying whole life policy from a mutual company — that lets you
      capture that banking function for yourself. When you take a policy loan to buy a car,
      you&apos;re borrowing from your own system instead of a lender. When you repay that loan,
      the money goes back into your system instead of a bank&apos;s. When your cash value keeps
      compounding while the loan is outstanding, your money is working in two places at once —
      the same mechanic the banks have been using for generations. You&apos;re not eliminating
      the need to finance things. You&apos;re changing who benefits when you do. That&apos;s what
      banks know. That controlling the flow of money — not earning the highest return on any
      single investment — is where the real financial advantage lives over a lifetime. Most
      people spend their entire lives funding that advantage for someone else. Some decide
      they&apos;d rather build it for themselves.
    </P>
  </>
);

export const ARTICLES = [
  {
    slug: "the-real-cost-of-financing",
    eyebrow: "Financial Systems",
    title: "The Real Cost of Financing — And Why Most People Never Add It Up",
    description: "The number most people never calculate — and what changes when they do.",
    image: ART1_IMG,
    imageAlt: "An open ledger showing handwritten lifetime interest calculations.",
    Body: Article1Body,
  },
  {
    slug: "why-ibc-isnt-what-your-financial-advisor-told-you",
    eyebrow: "Common Misconceptions",
    title: "Why IBC Isn't What Your Financial Advisor Told You",
    description: "The objections are common. The reasoning behind them is worth examining.",
    image: ART2_IMG,
    imageAlt: "A brass key resting beside a vintage whole life insurance policy and currency.",
    Body: Article2Body,
  },
  {
    slug: "what-banks-know-that-most-people-dont",
    eyebrow: "Financial Systems",
    title: "What Banks Know That Most People Don't",
    description: "How the banking system actually works — and what it means for your financial life.",
    image: ART3_IMG,
    imageAlt: "A circular diagram of money flowing through a bank as gold coins.",
    Body: Article3Body,
  },
];

export const findArticle = (slug) => ARTICLES.find((a) => a.slug === slug);
